/**
 * What was added to a dashboard, as a table.
 *
 * Derived entirely from the composed specification rather than from prose, so the
 * summary cannot disagree with the panels that were actually built. Each row
 * names the block, how it is drawn, and what it is broken down by, which is the
 * question a reader asks first when a dashboard appears.
 */
import type { DashboardComposedEvent } from "../../services/agui";

/** How each governed chart type is described to a reader. */
const VISUALISATION: Record<string, string> = {
  scalar: "Number (KPI tile)",
  bar: "Bar chart",
  line: "Line chart",
  pie: "Pie chart",
  table: "Table",
};

/** Turns a qualified member into something readable, matching the chart labels. */
function label(member: string): string {
  const short = member.split(".").pop() ?? member;
  return short.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

interface Props {
  event: DashboardComposedEvent;
}

export default function DashboardSummary({ event }: Props) {
  const panels = event.spec.panels ?? [];
  if (!panels.length) return null;

  // Named once at the end rather than repeated per row: every panel in a
  // composed dashboard is built from the same pinned contract.
  const datasets = Array.from(
    new Set(
      panels.flatMap((panel) =>
        [...panel.metrics, ...panel.dimensions]
          .map((member) => member.split(".")[0])
          .filter(Boolean),
      ),
    ),
  );

  return (
    <section className="dashboard-summary" aria-label="What was added">
      <p className="dashboard-summary-lead">
        Your <strong>{event.spec.title}</strong> dashboard is ready. Here is what
        was added:
      </p>
      <table>
        <thead>
          <tr>
            <th scope="col">Block</th>
            <th scope="col">Visualization</th>
            <th scope="col">Breakdown</th>
          </tr>
        </thead>
        <tbody>
          {panels.map((panel, index) => (
            <tr key={panel.id ?? `${panel.title}-${index}`}>
              <td>{panel.title}</td>
              <td>{VISUALISATION[panel.chartType] ?? panel.chartType}</td>
              <td>
                {panel.dimensions.length
                  ? `By ${panel.dimensions.map(label).join(", ")}`
                  : "\u2014"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {datasets.length ? (
        <p className="dashboard-summary-foot">
          Built from certified members in{" "}
          {datasets.map((dataset, index) => (
            <span key={dataset}>
              {index > 0 ? ", " : ""}
              <code>{dataset}</code>
            </span>
          ))}
          {event.spec.contextVersion
            ? ` (contract ${event.spec.contextId} ${event.spec.contextVersion}).`
            : `.`}
        </p>
      ) : null}
      {!event.saved ? (
        // The charts render either way, but offering to open something that was
        // never stored would send the reader looking for a dashboard that does
        // not exist.
        <p className="dashboard-summary-warning">
          This dashboard was rendered but not saved to your workspace
          {event.saveError ? `: ${event.saveError}` : "."}
        </p>
      ) : null}
    </section>
  );
}
